import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PropertyCard from "@/components/PropertyCard";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Heart, Home as HomeIcon, Trash2, Loader2, LogIn, Inbox, Mail, Phone, User as UserIcon, BadgeCheck, ShieldAlert, Car, Building2 } from "lucide-react";
import { formatINR, imgUrl } from "@/lib/format";
import { VerifyDialog } from "@/components/VerifyDialog";
import { VERTICALS } from "@/data/verticals";
import { V_ICONS } from "@/lib/verticalIcons";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [myProps, setMyProps] = useState([]);
  const [myVehicles, setMyVehicles] = useState([]);
  const [myMarket, setMyMarket] = useState([]);
  const [favs, setFavs] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [p, v, m, f, e] = await Promise.all([
        api.get("/my/properties"),
        api.get("/my/vehicles"),
        api.get("/my/listings"),
        api.get("/favorites"),
        api.get("/my/enquiries"),
      ]);
      setMyProps(p.data);
      setMyVehicles(v.data);
      setMyMarket(m.data);
      setFavs(f.data);
      setEnquiries(e.data);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  // Unified listings: properties + vehicles + marketplace verticals, newest first.
  const listings = [
    ...myProps.map((p) => ({ ...p, kind: "property" })),
    ...myVehicles.map((v) => ({ ...v, kind: "vehicle" })),
    ...myMarket.map((m) => ({ ...m, kind: "market" })),
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const remove = async (item) => {
    try {
      if (item.kind === "vehicle") {
        await api.delete(`/vehicles/${item.id}`);
        setMyVehicles((prev) => prev.filter((v) => v.id !== item.id));
      } else if (item.kind === "market") {
        await api.delete(`/listings/${item.id}`);
        setMyMarket((prev) => prev.filter((m) => m.id !== item.id));
      } else {
        await api.delete(`/properties/${item.id}`);
        setMyProps((prev) => prev.filter((p) => p.id !== item.id));
      }
      toast.success("Listing removed");
    } catch (e) {
      toast.error("Could not remove listing. Please try again.");
    }
  };

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center">
        <div className="h-14 w-14 rounded-2xl bg-navy/10 flex items-center justify-center mx-auto"><LogIn className="h-7 w-7 text-navy" /></div>
        <h1 className="mt-5 font-display text-2xl font-semibold text-slate-900">Sign in to view your dashboard</h1>
        <Button data-testid="dash-login-btn" onClick={login} className="mt-6 bg-navy hover:bg-navy-dark">Sign in with Google</Button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-10">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">
            Welcome, {user.name?.split(" ")[0]} 👋
          </h1>
          <p className="text-slate-500 mt-1">Manage your listings and shortlisted properties.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="dash-post-btn" onClick={() => navigate("/post")} className="bg-gold hover:bg-gold-dark text-navy font-semibold">
            <Building2 className="h-4 w-4 mr-1.5" /> Post Property
          </Button>
          <Button data-testid="dash-post-car-btn" onClick={() => navigate("/motors/post")} variant="outline" className="border-navy/30 text-navy hover:bg-navy/5 font-semibold">
            <Car className="h-4 w-4 mr-1.5" /> Post Car
          </Button>
        </div>
      </div>

      {/* Verification status card */}
      {user.verified ? (
        <div data-testid="verified-status-card" className="mt-6 flex items-center gap-4 bg-navy/5 border border-navy/15 rounded-2xl p-5">
          <div className="h-12 w-12 rounded-xl bg-navy flex items-center justify-center shrink-0">
            <BadgeCheck className="h-6 w-6 text-white" />
          </div>
          <div>
            <p className="font-display font-semibold text-navy">
              {user.verification_type === "nri" ? "NRI Verified 🌍" : "Identity Verified ✅"}
            </p>
            <p className="text-sm text-slate-500">Your profile and listings now show a trusted Verified badge.</p>
          </div>
        </div>
      ) : (
        <div data-testid="verify-prompt-card" className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4 bg-gold/10 border border-gold/40 rounded-2xl p-5">
          <div className="h-12 w-12 rounded-xl bg-gold flex items-center justify-center shrink-0">
            <ShieldAlert className="h-6 w-6 text-navy" />
          </div>
          <div className="flex-1">
            <p className="font-display font-semibold text-slate-900">Verify your identity to build trust</p>
            <p className="text-sm text-slate-500">Indian residents verify with Aadhaar + PAN, NRIs with passport. Required before posting a property.</p>
          </div>
          <VerifyDialog trigger={<Button data-testid="dash-verify-btn" className="bg-navy hover:bg-navy-dark text-white">Verify Now</Button>} />
        </div>
      )}

      {loading ? (
        <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-navy" /></div>
      ) : (
        <Tabs defaultValue="listings" className="mt-8">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="listings" data-testid="tab-listings"><HomeIcon className="h-4 w-4 mr-1.5" /> My Listings ({listings.length})</TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="tab-enquiries"><Inbox className="h-4 w-4 mr-1.5" /> Enquiries ({enquiries.length})</TabsTrigger>
            <TabsTrigger value="favorites" data-testid="tab-favorites"><Heart className="h-4 w-4 mr-1.5" /> Shortlist ({favs.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="listings" className="mt-6">
            {listings.length === 0 ? (
              <div className="py-20 text-center bg-white border border-slate-200 rounded-2xl">
                <p className="text-slate-500">You haven't listed anything yet.</p>
                <div className="mt-4 flex items-center justify-center gap-2">
                  <Button onClick={() => navigate("/post")} className="bg-navy hover:bg-navy-dark">Post a property</Button>
                  <Button onClick={() => navigate("/motors/post")} variant="outline" className="border-navy/30 text-navy hover:bg-navy/5">Post a car</Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {listings.map((item) => {
                  const isVehicle = item.kind === "vehicle";
                  const isMarket = item.kind === "market";
                  const vConf = isMarket ? VERTICALS[item.vertical] : null;
                  const to = isVehicle ? `/motors/${item.id}` : isMarket ? `/v/${item.vertical}/${item.id}` : `/properties/${item.id}`;
                  const BadgeIcon = isVehicle ? Car : isMarket ? (V_ICONS[vConf?.icon] || Building2) : Building2;
                  const badgeLabel = isVehicle ? "Car" : isMarket ? (vConf?.label || "Listing") : "Property";
                  const sub = isVehicle
                    ? `${item.year} · ${Number(item.km_driven).toLocaleString("en-IN")} km · ${item.city}`
                    : isMarket
                      ? `${item.specs?.brand ? item.specs.brand + " · " : ""}${item.city}`
                      : `${item.locality}, ${item.city}`;
                  return (
                    <div key={`${item.kind}-${item.id}`} data-testid={`my-listing-${item.id}`} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-4">
                      <img src={imgUrl(item.images?.[0])} alt="" className="h-20 w-28 object-cover rounded-lg cursor-pointer" onClick={() => navigate(to)} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span
                            data-testid={`listing-type-badge-${item.id}`}
                            className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${isVehicle ? "bg-navy/10 text-navy" : isMarket ? "bg-emerald-50 text-emerald-700" : "bg-gold/15 text-gold-dark"}`}
                          >
                            <BadgeIcon className="h-3 w-3" />
                            {badgeLabel}
                          </span>
                          {item.sold && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">Sold</span>}
                        </div>
                        <p className="font-display font-semibold text-slate-900 truncate mt-1 cursor-pointer" onClick={() => navigate(to)}>{item.title}</p>
                        <p className="text-sm text-slate-500 truncate">{sub}</p>
                        <p className="font-display font-bold text-navy mt-0.5">{formatINR(item.price)}</p>
                      </div>
                      <Button data-testid={`delete-listing-${item.id}`} variant="outline" onClick={() => remove(item)} className="border-gold/40 text-gold-dark hover:bg-gold/10">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="enquiries" className="mt-6">
            {enquiries.length === 0 ? (
              <div className="py-20 text-center bg-white border border-slate-200 rounded-2xl">
                <Inbox className="h-10 w-10 text-slate-300 mx-auto" />
                <p className="mt-3 text-slate-500">No enquiries yet. They'll appear here when buyers contact you about your listings.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {enquiries.map((e) => (
                  <div key={e.id} data-testid={`enquiry-${e.id}`} className="bg-white border border-slate-200 rounded-xl p-5">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        <span className="inline-block text-xs uppercase tracking-wide font-semibold text-gold-dark">Enquiry for</span>
                        <p className="font-display font-semibold text-slate-900">{e.property_title || "Property"}</p>
                      </div>
                      <span className="text-xs text-slate-400">{new Date(e.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                    </div>
                    <p className="mt-3 text-slate-700 text-sm bg-slate-50 border border-slate-100 rounded-lg p-3">{e.message}</p>
                    <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
                      <span className="flex items-center gap-1.5"><UserIcon className="h-4 w-4 text-navy" /> {e.name}</span>
                      <a href={`tel:${e.phone}`} className="flex items-center gap-1.5 hover:text-navy"><Phone className="h-4 w-4 text-navy" /> {e.phone}</a>
                      <a href={`mailto:${e.email}`} className="flex items-center gap-1.5 hover:text-navy"><Mail className="h-4 w-4 text-navy" /> {e.email}</a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="favorites" className="mt-6">
            {favs.length === 0 ? (
              <div className="py-20 text-center bg-white border border-slate-200 rounded-2xl">
                <Heart className="h-10 w-10 text-slate-300 mx-auto" />
                <p className="mt-3 text-slate-500">No saved properties yet. Tap the heart on any listing.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {favs.map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
